import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  TextField,
  Container,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import LocationCityIcon from "@material-ui/icons/LocationCity";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import CloseIcon from "@material-ui/icons/Close";
import { Formik } from "formik";
import { connect } from "react-redux";
import {
  async_update_event,
  uploadEventCoverImage,
  deleteImageFile,
} from "src/actions/event.action";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import PlacesAutocomplete from "react-places-autocomplete";
import { toast } from "react-toastify";
import TicketNameField, {
  ticketNamesProblem,
  withTrimmedNames,
} from "src/components/events/TicketNameField";
import { compressedFile, COUNTRIES } from "src/utils/misc";
import Autocomplete from "@mui/material/Autocomplete";

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
);

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  accentDark: "#000000",
  divider: "#F1F5F9",
};

const useStyles = makeStyles(() => ({
  root: { maxWidth: 960, margin: "0 auto", paddingTop: 24, paddingBottom: 48 },
  pageHeaderBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitle: { fontSize: 24, fontWeight: 700, color: TOKENS.textPrimary },
  pageSubtitle: { fontSize: 14, color: TOKENS.textSecondary, marginTop: 2 },
  section: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    marginBottom: 20,
    overflow: "hidden",
  },
  sectionHeader: { padding: "20px 24px 0 24px" },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: TOKENS.textPrimary },
  sectionSubtitle: { fontSize: 13, color: TOKENS.textSecondary, marginTop: 2 },
  sectionContent: { padding: "16px 24px 24px 24px" },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#A5B4FC" },
      "&.Mui-focused fieldset": { borderColor: TOKENS.accent },
    },
    "& .MuiInputLabel-root": { fontSize: 14, color: TOKENS.textSecondary },
  },
  selectField: {
    borderRadius: 8,
    fontSize: 14,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A5B4FC" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: TOKENS.accent,
    },
  },
  ticketRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    backgroundColor: "#FAFBFC",
    borderRadius: 10,
    border: `1px solid ${TOKENS.border}`,
    marginBottom: 8,
  },
  addRowBtn: {
    color: TOKENS.accent,
    borderColor: TOKENS.border,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  },
  coverPreview: {
    position: "relative",
    borderRadius: 12,
    overflow: "hidden",
    width: "100%",
    maxWidth: 360,
    aspectRatio: "16 / 9",
  },
  coverImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  coverRemoveBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.5)",
    color: "#FFFFFF",
    "&:hover": { backgroundColor: "#EF4444" },
  },
  pondWrapper: {
    "& .filepond--root": { fontFamily: "inherit" },
    "& .filepond--panel-root": {
      backgroundColor: "#FAFBFC",
      borderRadius: 12,
      border: `2px dashed ${TOKENS.border}`,
    },
    "& .filepond--drop-label": { color: TOKENS.textSecondary, fontSize: 14 },
    "& .filepond--label-action": {
      color: TOKENS.accent,
      fontWeight: 600,
      textDecorationColor: TOKENS.accent,
    },
    "& .filepond--item-panel": { borderRadius: 10 },
    "& .filepond--image-preview-wrapper": { borderRadius: 10 },
  },
  hint: { fontSize: 12, color: TOKENS.textMuted, marginTop: 8 },
  footerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 8,
    paddingTop: 16,
    borderTop: `1px solid ${TOKENS.border}`,
  },
  cancelButton: {
    color: TOKENS.textSecondary,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 500,
    textTransform: "none",
    "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
  },
  saveButton: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "8px 24px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": { backgroundColor: TOKENS.accentDark },
    "&:disabled": { backgroundColor: "#C7D2FE", color: "#FFFFFF" },
  },
  locationSuggestion: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: 8,
    marginTop: 4,
  },
}));

function EditEvent({
  event,
  async_update_event,
  uploadEventCoverImage,
  deleteImageFile,
}) {
  const classes = useStyles();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState(event.tickets || []);
  const [address, setAddress] = useState(`${event.address || ""}`);
  const [coverImage, setCoverImage] = useState(
    `${event.cover_image_url || ""}`,
  );
  const [country, setCountry] = useState(event.country || "Uganda");
  const [cover, setCover] = useState([]);
  const [coverFile, setCoverFile] = useState(null);

  useEffect(() => {
    setTickets(event.tickets || []);
    setAddress(event.address || "");
    setCoverImage(event.cover_image_url || "");
    setCountry(event.country || "Uganda");
  }, [event]);

  const handleTicketChange = (i, e) => {
    const { name, value } = e.target;
    setTickets(tickets.map((t, idx) => (idx === i ? { ...t, [name]: value } : t)));
  };

  const toDateInput = (d) => {
    try {
      return new Date(d).toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  return (
    <Container className={classes.root}>
      <Box className={classes.pageHeaderBar}>
        <Box>
          <Typography className={classes.pageTitle}>Edit Event</Typography>
          <Typography className={classes.pageSubtitle}>
            {event.event_name} — {event.country}, {event.district}
          </Typography>
        </Box>
      </Box>

      <Formik
        initialValues={event}
        enableReinitialize
        onSubmit={async (values) => {
          const ticketProblem = ticketNamesProblem(tickets);
          if (ticketProblem) return toast.warning(ticketProblem);
          const obj = {
            ...values,
            cover_image_url: coverFile || event.cover_image_url,
            address,
            tickets: withTrimmedNames(tickets),
            country,
          };
          await async_update_event(obj, event.id, navigate);
        }}
      >
        {({ handleBlur, handleChange, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            {/* Basic Info */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Basic Information
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Event name and location
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Event Name"
                      name="event_name"
                      defaultValue={event.event_name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disableClearable
                      fullWidth
                      options={COUNTRIES}
                      onChange={(_, v) => setCountry(v)}
                      inputValue={country}
                      onInputChange={(_, v) => setCountry(v)}
                      getOptionLabel={(o) => o}
                      defaultValue={event.country}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className={classes.textField}
                          variant="outlined"
                          label="Country"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="District / City"
                      name="district"
                      defaultValue={event.district}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PlacesAutocomplete
                      value={address}
                      onChange={setAddress}
                      onSelect={setAddress}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <div>
                          <TextField
                            {...getInputProps({
                              variant: "outlined",
                              label: "Google Maps Address",
                              fullWidth: true,
                              className: classes.textField,
                            })}
                          />
                          {(loading || suggestions.length > 0) && (
                            <Box
                              style={{
                                border: "1px solid #E2E8F0",
                                borderRadius: 8,
                                marginTop: 4,
                                overflow: "hidden",
                              }}
                            >
                              {loading && (
                                <Box style={{ padding: 12 }}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Searching...
                                  </Typography>
                                </Box>
                              )}
                              {suggestions.map((s) => (
                                <div
                                  {...getSuggestionItemProps(s, {
                                    className: classes.locationSuggestion,
                                    style: {
                                      backgroundColor: s.active
                                        ? "#F1F5F9"
                                        : "#FFFFFF",
                                    },
                                  })}
                                >
                                  <LocationCityIcon
                                    style={{
                                      marginRight: 10,
                                      color: TOKENS.textMuted,
                                      fontSize: 18,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    style={{ fontSize: 13 }}
                                  >
                                    {s.description}
                                  </Typography>
                                </div>
                              ))}
                            </Box>
                          )}
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Schedule */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Schedule
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  When does the event start and end?
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      required
                      fullWidth
                      label="Start Date"
                      name="start_date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      defaultValue={toDateInput(event.start_date)}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      required
                      fullWidth
                      label="Start Time"
                      name="start_time"
                      type="time"
                      InputLabelProps={{ shrink: true }}
                      defaultValue={event.start_time}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      required
                      fullWidth
                      label="End Date"
                      name="end_date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      defaultValue={toDateInput(event.end_date)}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      required
                      fullWidth
                      label="End Time"
                      name="end_time"
                      type="time"
                      InputLabelProps={{ shrink: true }}
                      defaultValue={event.end_time}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Description */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Description
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Tell guests about the event
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Brief Summary"
                      name="summary"
                      defaultValue={event.summary}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={3}
                      label="Full Description"
                      name="full_description"
                      defaultValue={event.full_description}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Extra Information"
                      name="extra_information"
                      defaultValue={event.extra_information}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Restrictions, dress code, etc."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Tickets */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Ticket Categories
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Set pricing tiers — {tickets.length} tier
                  {tickets.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                {tickets.map((ticket, idx) => (
                  <Box key={idx} className={classes.ticketRow}>
                    <TicketNameField
                      value={ticket.ticket_name}
                      onChange={(name) =>
                        handleTicketChange(idx, {
                          target: { name: "ticket_name", value: name },
                        })
                      }
                      takenNames={tickets
                        .filter((_, i) => i !== idx)
                        .map((t) => t.ticket_name)}
                      className={classes.textField}
                    />
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      label="Rate"
                      name="price_rate"
                      value={ticket.price_rate || ""}
                      onChange={(e) => handleTicketChange(idx, e)}
                      placeholder="Per person"
                      style={{ flex: 1 }}
                    />
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      label="Price (USD)"
                      name="ticket_price"
                      value={ticket.ticket_price || ""}
                      onChange={(e) => handleTicketChange(idx, e)}
                      type="number"
                      inputProps={{ min: 0 }}
                      style={{ width: 130 }}
                    />
                    {tickets.length > 1 && (
                      <IconButton
                        size="small"
                        style={{ color: "#EF4444" }}
                        onClick={() =>
                          setTickets(tickets.filter((_, i) => i !== idx))
                        }
                      >
                        <RemoveIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {tickets.length < 4 && (
                  <Button
                    variant="outlined"
                    className={classes.addRowBtn}
                    startIcon={<AddIcon style={{ fontSize: 16 }} />}
                    onClick={() =>
                      setTickets([
                        ...tickets,
                        { ticket_name: "", price_rate: "", ticket_price: "" },
                      ])
                    }
                  >
                    Add Ticket Tier
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Cover Image */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Cover Image
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Current cover image for your event
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                {coverImage ? (
                  <Box style={{ marginBottom: 16 }}>
                    <Box className={classes.coverPreview}>
                      <img
                        src={coverImage}
                        alt="Cover"
                        className={classes.coverImage}
                      />
                      <IconButton
                        className={classes.coverRemoveBtn}
                        size="small"
                        onClick={() => {
                          setCoverImage("");
                          setCoverFile(null);
                        }}
                      >
                        <CloseIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box className={classes.pondWrapper}>
                    <FilePond
                      files={cover}
                      onupdatefiles={setCover}
                      allowMultiple={false}
                      allowImageResize={true}
                      allowRemove={true}
                      acceptedFileTypes={["image/*"]}
                      server={{
                        process: async (
                          fieldName,
                          file,
                          metadata,
                          load,
                          error,
                          progress,
                          abort,
                        ) => {
                          try {
                            const image = await compressedFile(file);
                            const formData = new FormData();
                            formData.append("file", image);
                            const res = await uploadEventCoverImage(formData);
                            setCoverFile(res);
                            load();
                          } catch (err) {
                            error("Upload failed");
                          }
                          return { abort: () => abort() };
                        },
                        revert: async (uid, load) => {
                          try {
                            if (coverFile?.Key)
                              await deleteImageFile({ Key: coverFile.Key });
                            setCoverFile(null);
                            load();
                          } catch {
                            load();
                          }
                        },
                      }}
                      labelIdle='Drag & drop image or <span class="filepond--label-action">Browse</span>'
                    />
                    <Typography className={classes.hint}>
                      Max 2 MB. JPG, PNG, or WebP.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Footer */}
            <Box className={classes.footerActions}>
              <Button
                variant="outlined"
                className={classes.cancelButton}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                className={classes.saveButton}
              >
                Update Event
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Container>
  );
}

const mapStateToProps = (state) => ({ event: state.eventReducer.event });

export default connect(mapStateToProps, {
  async_update_event,
  uploadEventCoverImage,
  deleteImageFile,
})(EditEvent);
